// CallEngine — the composition root of the call module (docs/06 §3). Owns the
// state machine, the signaling client, the peer connection, crypto
// verification and the quality monitor; publishes one immutable
// CallEngineState snapshot through a Store that React subscribes to. All
// platform capabilities (identity, storage, sockets, WebRTC, media, timers)
// are injected — nothing here touches a browser global directly.

import {
  type CallAcceptPayload,
  type CallIcePayload,
  type CallInvitePayload,
  type CallRenegotiatePayload,
  type CallSyncPayload,
  type EndpointList,
  type IceCandidate,
  type IceServer,
  type WsFrame,
} from '@merkury/protocol';
import { CallMachine, isPolite, type Effect, type MachineEvent, type TimerId } from './machine.js';
import { reconcileSync, remainingRingMs } from './reconcile.js';
import { SignalingClient } from './signaling/client.js';
import { verifySdpFingerprint, signSdpFingerprint, publicKeyFingerprint } from './crypto/sdpAuth.js';
import { KeyPins } from './crypto/pinning.js';
import { deriveSafetyNumber, type SafetyNumber } from './crypto/safetyNumber.js';
import type { Keyring, VerifyResult } from './crypto/endpointList.js';
import { QualityMonitor } from './quality/monitor.js';
import { RUNG_CONFIGS, type LadderDecision, type RungConfig, type StatsSample } from './quality/ladder.js';
import { stunFallbackFrom } from './iceServers.js';
import { applyOpusConfig, opusConfigForRung, type OpusConfig } from './rtc/sdp.js';
import {
  PeerHandle,
  sdpTypeFromMarker,
  withSdpTypeMarker,
  type MediaAdapter,
  type MediaStreamLike,
  type PeerConnectionFactory,
  type PeerConnectionLike,
} from './rtc/peer.js';
import { Store, type ReadableStore } from './store.js';
import {
  INITIAL_ENGINE_STATE,
  REAL_TIMERS,
  type AccessTokenProvider,
  type ActiveCallState,
  type AudioRouteRequest,
  type AudioRouteState,
  type CallEngineState,
  type ContactsProvider,
  type IceServersProvider,
  type IdentitySigner,
  type KeyPinStorage,
  type TimerHost,
  type WebSocketFactory,
} from './types.js';

const MAX_CACHED_INVITES = 8;

/**
 * Floor between rung-driven re-offers. Rung changes cluster on a bad link,
 * which is exactly when the signaling socket can least afford a burst of
 * offers — and each re-offer costs a round trip the call is already short of.
 */
const RUNG_RENEGOTIATE_MIN_INTERVAL_MS = 15_000;

/** getUserMedia failure shapes (permission denied, no/busy device). */
const MEDIA_CAPTURE_ERRORS: ReadonlySet<string> = new Set([
  'NotAllowedError',
  'SecurityError',
  'NotFoundError',
  'NotReadableError',
  'OverconstrainedError',
]);

function isMediaCaptureError(err: unknown): boolean {
  return err instanceof Error && MEDIA_CAPTURE_ERRORS.has(err.name);
}

/**
 * A repeating call-progress tone. Structurally the `TonePlayer` of
 * `screens/call/reconnectTone.ts` (declared here so the call module never
 * references the UI layer, not even in a type). Both calls must be idempotent.
 */
export interface CallTonePlayer {
  start(): void;
  stop(): void;
}

/** The two tones the engine owns when a host hands them over (see setCallTones). */
export interface CallProgressTones {
  /** Soft beep while `reconnecting` (docs/06 §5 table). */
  reconnect: CallTonePlayer;
  /** Caller-side ringback while the remote end rings. */
  ringback: CallTonePlayer;
}

export interface CallEngineDeps {
  identity: IdentitySigner;
  contacts: ContactsProvider;
  pinStorage: KeyPinStorage;
  /** Pinned endpoint-list signer keys (baked into the build). */
  keyring: Keyring;
  /** Verified endpoint list to boot with (baked-in floor or persisted copy). */
  endpointList: EndpointList;
  lastGoodUrl?: string;
  persistEndpoints?: (state: { list: EndpointList; lastGoodUrl?: string }) => void;
  getToken: AccessTokenProvider;
  getIceServers: IceServersProvider;
  ws: WebSocketFactory;
  pc: PeerConnectionFactory;
  media: MediaAdapter;
  timers?: TimerHost;
  newCallId?: () => string;
  /** Non-call frames (presence, friend events) passed through to the host. */
  onFrame?: (frame: WsFrame) => void;
}

export class CallEngine {
  private readonly store = new Store<CallEngineState>(INITIAL_ENGINE_STATE);
  private readonly machine: CallMachine;
  private readonly client: SignalingClient;
  private readonly pins: KeyPins;
  private readonly monitor: QualityMonitor;
  private readonly timers: TimerHost;

  private peer: PeerHandle | null = null;
  /**
   * The callId this.peer was opened for. The machine switches calls
   * synchronously but openPeer/closePeer run later in the effect pipeline, so
   * during a supersede the machine's callId and the live pc briefly disagree —
   * frames gated only on the machine's callId would hit the OLD pc (a
   * candidate applied there is lost for the new call).
   */
  private peerCallId: string | null = null;
  private localStream: MediaStreamLike | null = null;
  private readonly timerHandles = new Map<TimerId, unknown>();
  private readonly cachedInvites = new Map<string, CallInvitePayload>();
  /**
   * Remote trickle candidates not yet applicable: they precede our
   * setRemoteDescription (callee still ringing), or belong to a call that is
   * not current yet (the incoming half of a crossed call, whose peer object
   * only exists after the deferred openPeer effect runs). Keyed by callId,
   * bounded, applied as soon as the call's remote description is in place,
   * dropped on call end.
   */
  private readonly pendingRemoteIce = new Map<string, IceCandidate[]>();
  private remoteApplied = false;
  /**
   * True once our session description frame (invite/accept) has been SENT.
   * Local candidates can be ready during the async signing that precedes the
   * send — flushing them early would deliver `call.ice` before `call.invite`
   * and the peer would drop them. Held until the description is on the wire.
   */
  private descriptionSent = false;
  private outgoingHold: IceCandidate[] = [];
  private makingOffer = false;
  private polite = false;
  private relayOnly = false;
  /**
   * ICE servers fetched for the current peer connection. Cached so a mid-call
   * setConfiguration (rung-4 force-relay and its recovery) can flip
   * iceTransportPolicy WITHOUT dropping the TURN servers — passing an empty
   * list there would leave a relay-only PC with nothing to gather, stranding
   * the call in "reconnecting" forever (Connection > everything).
   */
  private currentIceServers: IceServer[] = [];
  /**
   * True when currentIceServers is the credential-free STUN floor rather than
   * real TURN credentials. A hard ICE failure is precisely when relay is
   * needed, so the recovery path must still re-fetch in this state — without
   * this flag the fallback would look like "we already have servers" and
   * silently disable that recovery.
   */
  private iceFallbackOnly = false;
  private muted = false;
  private speaker = false;
  private tones: CallProgressTones | null = null;
  private reconnectToneOn = false;
  private ringbackOn = false;
  private audioRequest: AudioRouteRequest = 'AUTO';
  private aux: Pick<ActiveCallState, 'connectedAt' | 'reconnectingSince' | 'rung' | 'bar' | 'viaRelay'> =
    { connectedAt: null, reconnectingSince: null, rung: 0, bar: 'green', viaRelay: false };
  private appliedRung = -1;
  /** Opus shape currently negotiated, so a rung change can tell whether the
   *  SDP actually needs re-offering (see sdpShapeChanged). */
  private shapedOpus: OpusConfig | null = null;
  /** -Infinity, not 0: zero reads as "renegotiated at t=0" and suppresses the
   *  FIRST rung-driven re-offer, which is the one that matters most. */
  private lastRungRenegotiateAt = Number.NEGATIVE_INFINITY;

  /** Serializes async effect execution so frames can't interleave mid-flow. */
  private pipeline: Promise<void> = Promise.resolve();

  constructor(private readonly deps: CallEngineDeps) {
    this.timers = deps.timers ?? REAL_TIMERS;
    this.machine = new CallMachine(deps.identity.userId);
    this.pins = new KeyPins(deps.pinStorage);
    this.monitor = new QualityMonitor({
      getStats: () => this.peer?.pc.getStats?.() ?? Promise.resolve(null),
      timers: this.timers,
      onDecision: (d, s) => this.onQualityDecision(d, s),
      onRtpStalled: () => this.drive({ t: 'rtpStalled' }),
      onRtpTimeout: () => this.drive({ t: 'rtpTimeout' }),
    });
    this.client = new SignalingClient({
      getToken: deps.getToken,
      ws: deps.ws,
      timers: this.timers,
      keyring: deps.keyring,
      initialList: deps.endpointList,
      lastGoodUrl: deps.lastGoodUrl,
      ...(deps.persistEndpoints ? { persist: deps.persistEndpoints } : {}),
      onFrame: (frame) => this.routeFrame(frame),
      onStatus: (status) => {
        this.store.patch({ signaling: status, endpointListVersion: this.client.listVersion });
        // Warm the TURN credentials while nobody is waiting. They are otherwise
        // fetched on the critical path of the first dial/accept — on a
        // doze-woken phone that is exactly when the radio is coldest, so the
        // round trip lands on the call's setup budget instead of on idle time.
        // The provider caches for the credentials' TTL, so this is one request.
        if (status === 'connected') this.prewarmIceServers();
      },
    });
    this.store.patch({ endpointListVersion: deps.endpointList.version });
  }

  /** The published state J9's screens subscribe to. */
  get state(): ReadableStore<CallEngineState> {
    return this.store;
  }

  /**
   * Fill the ICE-server cache off the critical path. Deliberately fire-and-
   * forget and completely silent: a failure here costs nothing, because the
   * dial path fetches them anyway and the provider has its own retry and
   * cached-credential fallback.
   */
  private prewarmIceServers(): void {
    void this.deps.getIceServers().catch(() => undefined);
  }

  /**
   * ICE servers for a peer connection, with a credential-free floor.
   *
   * A TURN allocation needs credentials from the API, so when the API is
   * unreachable this used to resolve to an empty list and the peer connection
   * was left with host candidates only — no NAT traversal at all, i.e. two
   * phones on mobile networks simply could not connect. STUN needs no
   * credentials, and the signed endpoint list already names the same hosts, so
   * we can still learn our server-reflexive address and punch through most
   * NATs. Relay stays unavailable, which is the correct degradation: worse odds
   * of connecting, rather than none (Connection > everything).
   */
  private async iceServersOrFallback(): Promise<IceServer[]> {
    const servers = await this.deps.getIceServers().catch(() => []);
    if (servers.length > 0) {
      this.iceFallbackOnly = false;
      return servers;
    }
    const fallback = stunFallbackFrom(this.client.endpointList);
    this.iceFallbackOnly = fallback.length > 0;
    return fallback;
  }

  private readonly strayEndHandlers = new Set<(callId: string) => void>();

  /**
   * Fires when a `call.end` arrives for a call the machine holds no live
   * state for (the shadowed half of a crossed pair; an invite only the native
   * push receiver rang). Hosts with out-of-band ring surfaces use it to
   * dismiss rings the engine itself never showed. Returns an unsubscribe.
   */
  onStrayEnd(handler: (callId: string) => void): () => void {
    this.strayEndHandlers.add(handler);
    return () => this.strayEndHandlers.delete(handler);
  }

  start(): void {
    this.client.start();
  }

  stop(): void {
    if (this.machine.state.phase !== 'idle') this.drive({ t: 'hangup' });
    this.stopTones();
    this.client.stop();
  }

  /**
   * Hand the call-progress tones to the engine, or `null` to take them back
   * (stopping both immediately).
   *
   * The tones are phase-driven, not screen-driven: the Android build renders no
   * WebView UI at all, so nothing above the engine is left to start them, and
   * reconnect feedback is exactly the audio a user on a degrading link must not
   * lose (prime directive). Hosts that still render their own call screen — the
   * web app — keep owning them there and never call this.
   *
   * Incoming ringing is deliberately NOT here: Android rings natively and the
   * web's IncomingCallScreen owns its own ringtone.
   */
  setCallTones(tones: CallProgressTones | null): void {
    if (this.tones === tones) return;
    this.stopTones();
    this.tones = tones;
    this.syncTones();
  }

  /** The live capture stream, for host-side metering. Null outside a call. */
  get localAudioStream(): MediaStreamLike | null {
    return this.localStream;
  }

  // --- user intents -----------------------------------------------------------

  /**
   * Start an outgoing call. Resolves false (without dialing) when the peer is
   * unknown or their key changed — the latter publishes the key-change
   * interlock for the UI to confirm first.
   */
  async dial(peerUserId: string): Promise<boolean> {
    const st = this.machine.state;
    // Glare, non-simultaneous variant (docs/02 §4): the peer's invite already
    // landed and we're ringing THEM — dialing the very person calling us is the
    // same intent as accepting. Auto-accept so exactly one call connects and
    // neither side sees "busy" (the invite's key/signature were already checked
    // in onInvite before it started ringing). The simultaneous both-dialing
    // variant is handled in the state machine's crossed tiebreak.
    if (st.phase === 'ringing' && st.call?.peer === peerUserId) {
      this.accept();
      return true;
    }
    if (st.phase !== 'idle') return false;
    // Directory first (it can legitimately rotate a key), then the local pin —
    // the other key lookups (verificationKeyFor, safetyNumberWith) already fall
    // back this way. Without it a peer we have called before becomes
    // undialable the moment the directory is unreachable, which is precisely
    // when a call matters most (prime directive: connection above all).
    const key =
      (await this.deps.contacts.getPublicKey(peerUserId)) ??
      (await this.pins.pinnedKey(peerUserId));
    if (!key) return false;
    const pin = await this.pins.checkAndPin(peerUserId, key);
    if (pin.status === 'changed') {
      this.store.patch({
        keyChange: { peerUserId, pinnedKeyB64: pin.pinnedKeyB64, newKeyB64: key },
      });
      return false;
    }
    const callId = (this.deps.newCallId ?? (() => crypto.randomUUID()))();
    this.store.patch({ lastEnded: null });
    this.drive({ t: 'dial', callId, peer: peerUserId });
    return true;
  }

  accept(): void {
    this.drive({ t: 'accept' });
  }

  decline(): void {
    this.drive({ t: 'decline' });
  }

  hangup(): void {
    this.drive({ t: 'hangup' });
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    for (const track of this.localStream?.getAudioTracks() ?? []) {
      // track.enabled=false keeps encryption & timing alive (docs/06 §3).
      track.enabled = !muted;
    }
    this.publish();
  }

  setSpeaker(on: boolean): void {
    this.speaker = on;
    this.audioRequest = on ? 'SPEAKER' : 'AUTO';
    this.publish();
  }

  /**
   * Explicit in-call route choice (native route control): mirrored into the
   * legacy speaker boolean so the web output path keeps working. The native
   * side resets to AUTO at call end / when a new device connects; the shell
   * reports that back via noteAudioRoute.
   */
  requestAudioRoute(route: AudioRouteRequest): void {
    this.audioRequest = route;
    this.speaker = route === 'SPEAKER';
    this.publish();
  }

  /** Native feedback: what the audio actually plays on (shell wiring). */
  noteAudioRoute(state: AudioRouteState): void {
    this.store.patch({ audio: state });
  }

  /** Host calls this on OS-level network change (Wi-Fi ↔ LTE). */
  noteNetworkChange(): void {
    this.drive({ t: 'networkChange' });
  }

  /** Home screen "Retry now": re-race the endpoint list immediately. */
  retrySignaling(): void {
    this.client.retryNow();
  }

  /** User accepted a "safety key changed" warning: repin and unblock. */
  async confirmKeyChange(): Promise<void> {
    const alert = this.store.get().keyChange;
    if (!alert) return;
    await this.pins.acceptChange(alert.peerUserId, alert.newKeyB64);
    this.store.patch({ keyChange: null });
  }

  dismissKeyChange(): void {
    this.store.patch({ keyChange: null });
  }

  async safetyNumberWith(peerUserId: string): Promise<SafetyNumber | null> {
    const key =
      (await this.pins.pinnedKey(peerUserId)) ?? (await this.deps.contacts.getPublicKey(peerUserId));
    if (!key) return null;
    return deriveSafetyNumber(this.deps.identity.publicKeyB64, key);
  }

  /** Feed a (possibly hostile) endpoint-list document from any channel. */
  async updateEndpointList(input: unknown): Promise<VerifyResult> {
    const result = await this.client.updateEndpointList(input);
    if (result.ok) this.store.patch({ endpointListVersion: this.client.listVersion });
    return result;
  }

  // --- machine driving ---------------------------------------------------------

  private drive(ev: MachineEvent): void {
    const effects = this.machine.dispatch(ev);
    if (effects.length > 0) {
      this.pipeline = this.pipeline
        .then(() => this.execAll(effects))
        .catch((err) => this.abortBrokenCall(err));
    }
    this.publish();
  }

  /** An effect crashed (getUserMedia denied, SDP failure…): fail the call cleanly. */
  private abortBrokenCall(err?: unknown): void {
    if (this.machine.state.phase === 'idle') return;
    // A mic-capture failure is the user's own device saying no — surface it
    // as mic_denied so the UI can point at the permission, not at security.
    const effects = this.machine.dispatch(
      isMediaCaptureError(err) ? { t: 'mediaAbort' } : { t: 'securityAbort' },
    );
    void this.execAll(effects).catch(() => {
      /* teardown must not throw */
    });
    this.publish();
  }

  private async execAll(effects: Effect[]): Promise<void> {
    for (const fx of effects) await this.exec(fx);
    this.publish();
  }

  private async exec(fx: Effect): Promise<void> {
    switch (fx.e) {
      case 'openPeer': {
        this.polite = isPolite(this.deps.identity.userId, fx.peer);
        this.peerCallId = fx.callId;
        this.remoteApplied = false;
        this.descriptionSent = false;
        this.outgoingHold = [];
        this.appliedRung = -1;
        this.shapedOpus = null;
        this.lastRungRenegotiateAt = Number.NEGATIVE_INFINITY;
        this.aux = { connectedAt: null, reconnectingSince: null, rung: 0, bar: 'green', viaRelay: false };
        // Audio preferences are PER CALL, not per engine. They used to persist
        // for the engine's lifetime, so muting once left `muted` true for every
        // later call — and this is not cosmetic: the track publish below applies
        // `track.enabled = !this.muted`, so the next call went out with a dead
        // mic. Reset here, not on call end: `openPeer` is emitted only when a
        // call STARTS (dial / accept / glare-accept in machine.ts), never on a
        // reconnect or ICE restart, so an in-call mute survives a flap.
        this.muted = false;
        this.speaker = false;
        this.audioRequest = 'AUTO';
        const iceServers = await this.iceServersOrFallback();
        this.currentIceServers = iceServers;
        const pc = this.deps.pc({
          iceServers,
          iceTransportPolicy: this.relayOnly ? 'relay' : 'all',
        });
        this.peer = new PeerHandle(pc, this.timers, {
          onCandidateBatch: (candidates) => {
            if (!this.descriptionSent) {
              this.outgoingHold.push(...candidates);
              return;
            }
            this.client.send({ type: 'call.ice', payload: { callId: fx.callId, candidates } });
          },
          onIceState: (state) => this.onIceState(state),
          onRemoteStream: (stream) => {
            // Initial arrival and any renegotiation that replaces the remote
            // track publish here; the shell attaches it to its audio sink.
            if (this.store.get().remoteStream !== stream) {
              this.store.patch({ remoteStream: stream });
            }
          },
        });
        this.localStream = await this.deps.media.getLocalAudio();
        for (const track of this.localStream.getAudioTracks()) {
          track.enabled = !this.muted;
          pc.addTrack?.(track, this.localStream);
        }
        if (fx.role === 'caller') await this.sendInvite(fx.callId, fx.peer, pc);
        break;
      }

      case 'closePeer': {
        this.monitor.stop();
        this.peer?.close();
        this.peer = null;
        this.peerCallId = null;
        for (const track of this.localStream?.getAudioTracks() ?? []) track.stop?.();
        this.localStream = null;
        if (this.store.get().remoteStream !== null) this.store.patch({ remoteStream: null });
        this.remoteApplied = false;
        this.descriptionSent = false;
        this.outgoingHold = [];
        this.makingOffer = false;
        this.relayOnly = false;
        this.currentIceServers = [];
        break;
      }

      case 'sendRinging':
        this.client.send({ type: 'call.ringing', payload: { callId: fx.callId } });
        break;

      case 'sendAccept': {
        const invite = this.cachedInvites.get(fx.callId);
        const pc = this.peer?.pc;
        if (!invite || !pc) throw new Error('accept without cached invite/peer');
        await pc.setRemoteDescription({ type: 'offer', sdp: invite.sdp });
        this.remoteApplied = true;
        await this.applyHeldCandidates(fx.callId);
        const answer = await pc.createAnswer();
        const sdp = await this.setShapedLocalDescription(pc, answer);
        const fingerprintSig = await signSdpFingerprint(this.deps.identity, {
          callId: fx.callId,
          toUuid: invite.from,
          sdp,
        });
        this.client.send({ type: 'call.accept', payload: { callId: fx.callId, sdp, fingerprintSig } });
        this.markDescriptionSent(fx.callId);
        break;
      }

      case 'sendDecline':
        this.client.send({
          type: 'call.decline',
          payload: { callId: fx.callId, reason: fx.reason },
        });
        break;

      case 'sendEnd':
        this.client.send({ type: 'call.end', payload: { callId: fx.callId, reason: fx.reason } });
        break;

      case 'restartIce': {
        const pc = this.peer?.pc;
        if (!pc) break;
        if (this.currentIceServers.length === 0 || this.iceFallbackOnly) {
          // The call was set up without usable ICE servers — either none at all
          // or only the credential-free STUN floor (the credentials fetch
          // failed at dial time). A restart with the same config would fail
          // identically, and a hard ICE failure is exactly the case that needs
          // relay. Re-fetch before restarting; the provider retries and serves
          // cached credentials (iceServers.ts).
          const servers = await this.iceServersOrFallback();
          if (servers.length > 0) {
            this.currentIceServers = servers;
            pc.setConfiguration?.({
              iceServers: servers,
              iceTransportPolicy: this.relayOnly ? 'relay' : 'all',
            });
          }
        }
        this.monitor.noteIceRestart();
        pc.restartIce?.();
        await this.sendRenegotiateOffer(pc);
        break;
      }

      case 'startTimer': {
        this.clearTimer(fx.which);
        this.timerHandles.set(
          fx.which,
          this.timers.setTimeout(() => {
            this.timerHandles.delete(fx.which);
            this.drive({ t: 'timer', which: fx.which });
          }, fx.ms),
        );
        break;
      }

      case 'stopTimer':
        this.clearTimer(fx.which);
        break;

      case 'callEnded': {
        this.cachedInvites.delete(fx.callId);
        this.pendingRemoteIce.delete(fx.callId);
        if (fx.cause === 'connection_lost' || fx.cause === 'failed') this.monitor.stop();
        this.store.patch({
          lastEnded: {
            callId: fx.callId,
            peerUserId: fx.peer,
            direction: fx.direction,
            cause: fx.cause,
            endedAt: this.timers.now(),
            // aux still holds this call's connect time here (reset only on the
            // next call's openPeer), so history knows it connected even when
            // the active `call` was already cleared in an earlier emit.
            connectedAt: this.aux.connectedAt,
          },
        });
        break;
      }
    }
  }

  private clearTimer(which: TimerId): void {
    const handle = this.timerHandles.get(which);
    if (handle !== undefined) {
      this.timers.clearTimeout(handle);
      this.timerHandles.delete(which);
    }
  }

  // --- outgoing negotiation ------------------------------------------------------

  /**
   * Configure Opus on a freshly created local description (docs/01 §4.2). The
   * knobs only exist in SDP, and a local description's fmtp/ptime configure
   * the PEER's encoder — both ends run this, so both directions are covered.
   *
   * Ordering is load-bearing: this runs BEFORE setLocalDescription so the
   * description we apply, sign and send is one string. It never touches
   * a=fingerprint (the only line signSdpFingerprint hashes), so the E2EE
   * signature is unaffected by the munge — and the peer, which verifies the
   * same line out of the SDP it receives, still sees identical bytes.
   *
   * The frame size comes from the current rung, so a renegotiation on a
   * degraded link (an ICE restart at rung 4) re-offers with long frames.
   */
  private shapeLocalSdp(sdp: string): string {
    const rung = RUNG_CONFIGS[this.aux.rung] ?? RUNG_CONFIGS[0]!;
    const config = opusConfigForRung(rung);
    // Record what we are about to negotiate, so a later rung change can tell
    // whether it actually needs a re-offer (sdpShapeChanged).
    this.shapedOpus = config;
    try {
      return applyOpusConfig(sdp, config);
    } catch {
      return sdp; // never let codec tuning break a call
    }
  }

  /**
   * setLocalDescription with the shaped SDP, falling back to the untouched one.
   *
   * Munging a local description is the only way to reach Opus's knobs, but it
   * is also something a browser is allowed to reject — and an unconfigured
   * codec is merely worse audio, while a rejected description is NO CALL.
   * Prime directive: degrade, never drop. Returns the SDP actually applied, so
   * the fingerprint signature is always computed over what the peer will see.
   */
  private async setShapedLocalDescription(
    pc: PeerConnectionLike,
    description: { type: string; sdp: string },
  ): Promise<string> {
    const shaped = this.shapeLocalSdp(description.sdp);
    if (shaped !== description.sdp) {
      try {
        await pc.setLocalDescription({ type: description.type, sdp: shaped });
        return pc.localDescription?.sdp ?? shaped;
      } catch {
        // Fall through to the description the peer connection produced.
      }
    }
    // Nothing shaped actually reached the wire — either there was no Opus
    // section to configure or the browser refused the munged description — so
    // record no negotiated shape rather than claim one.
    this.shapedOpus = null;
    await pc.setLocalDescription(description);
    return pc.localDescription?.sdp ?? description.sdp;
  }

  private async sendInvite(callId: string, peer: string, pc: PeerConnectionLike): Promise<void> {
    this.makingOffer = true;
    try {
      const offer = await pc.createOffer();
      const sdp = await this.setShapedLocalDescription(pc, offer);
      const fingerprintSig = await signSdpFingerprint(this.deps.identity, {
        callId,
        toUuid: peer,
        sdp,
      });
      const callerKeyFingerprint = await publicKeyFingerprint(this.deps.identity.publicKeyB64);
      this.client.send({
        type: 'call.invite',
        payload: {
          callId,
          from: this.deps.identity.userId,
          to: peer,
          sdp,
          fingerprintSig,
          callerKeyFingerprint,
        },
      });
      this.markDescriptionSent(callId);
    } finally {
      this.makingOffer = false;
    }
  }

  /** The description frame is on the wire: release held local candidates. */
  private markDescriptionSent(callId: string): void {
    this.descriptionSent = true;
    if (this.outgoingHold.length > 0) {
      const candidates = this.outgoingHold;
      this.outgoingHold = [];
      this.client.send({ type: 'call.ice', payload: { callId, candidates } });
    }
  }

  /**
   * Whether this rung changes something that only an SDP re-offer can carry.
   *
   * Pure on purpose. `shapedOpus` is written where a description is actually
   * produced (shapeLocalSdp), never here: recording intent at the decision
   * point meant that every bail-out in renegotiateForRung — the rate-limit
   * floor especially — left the engine believing it had negotiated a shape it
   * never sent, and since applyRungConfig only runs on a rung CHANGE, nothing
   * later could correct it. A call could then sit on narrowband 60 ms framing
   * for its whole life on a link that had recovered.
   */
  private sdpShapeChanged(config: RungConfig): boolean {
    const next = opusConfigForRung(config);
    const previous = this.shapedOpus;
    if (!previous) return false; // nothing negotiated yet; setup will carry it
    return (
      previous.ptimeMs !== next.ptimeMs ||
      previous.maxPlaybackRateHz !== next.maxPlaybackRateHz ||
      previous.red !== next.red
    );
  }

  /**
   * Re-offer with the current rung's Opus shape. Rate-limited, and a no-op
   * unless the call is up and the peer connection is idle: rung changes cluster
   * on a bad link, which is exactly when the signaling socket is least able to
   * carry a burst of offers. A failed or dropped re-offer leaves the call
   * untouched — worse audio parameters beat a disturbed call.
   */
  private renegotiateForRung(): void {
    const pc = this.peer?.pc;
    const now = this.timers.now();
    if (!pc || this.makingOffer || this.machine.state.phase !== 'in_call') return;
    if (pc.signalingState !== 'stable') return;
    if (now - this.lastRungRenegotiateAt < RUNG_RENEGOTIATE_MIN_INTERVAL_MS) return;
    this.lastRungRenegotiateAt = now;
    this.enqueue(async () => {
      try {
        await this.sendRenegotiateOffer(pc, { iceRestart: false });
      } catch {
        // Codec shape is an optimisation; never let it end a call.
      }
    });
  }

  private async sendRenegotiateOffer(
    pc: PeerConnectionLike,
    { iceRestart = true }: { iceRestart?: boolean } = {},
  ): Promise<void> {
    const call = this.machine.state.call;
    if (!call) return;
    this.makingOffer = true;
    try {
      const offer = await pc.createOffer({ iceRestart });
      const sdp = withSdpTypeMarker(await this.setShapedLocalDescription(pc, offer), 'offer');
      const fingerprintSig = await signSdpFingerprint(this.deps.identity, {
        callId: call.callId,
        toUuid: call.peer,
        sdp,
      });
      this.client.send({
        type: 'call.renegotiate',
        payload: { callId: call.callId, sdp, fingerprintSig },
      });
    } finally {
      this.makingOffer = false;
    }
  }

  // --- incoming frames -------------------------------------------------------------

  private routeFrame(frame: WsFrame): void {
    switch (frame.type) {
      case 'call.invite':
        this.enqueue(() => this.onInvite(frame.payload));
        return;
      case 'call.ringing':
        this.drive({ t: 'remoteRinging', callId: frame.payload.callId });
        return;
      case 'call.accept':
        this.enqueue(() => this.onAccept(frame.payload));
        return;
      case 'call.decline':
        this.drive({
          t: 'remoteDecline',
          callId: frame.payload.callId,
          reason: frame.payload.reason,
        });
        return;
      case 'call.ice':
        this.enqueue(() => this.onIce(frame.payload));
        return;
      case 'call.renegotiate':
        this.enqueue(() => this.onRenegotiate(frame.payload));
        return;
      case 'call.end': {
        this.cachedInvites.delete(frame.payload.callId);
        const live = this.machine.state.call;
        if (!live || live.callId !== frame.payload.callId) {
          // An end for a call we never surfaced (e.g. the shadowed half of a
          // crossed pair, or an invite only the native push receiver rang):
          // give hosts a chance to dismiss a native ring for it — the
          // machine will ignore the event, so nobody else can.
          for (const handler of this.strayEndHandlers) handler(frame.payload.callId);
        }
        this.drive({ t: 'remoteEnd', callId: frame.payload.callId, reason: frame.payload.reason });
        return;
      }
      case 'call.sync':
        this.onSync(frame.payload);
        return;
      case 'error': {
        // Bare error frames carry no callId, but per-connection ordering makes
        // one that lands while OUR invite is in flight the server's refusal of
        // it (not_friends after an unfriend/block, conflict, forbidden).
        // Without this the caller fake-rings for the whole ring timeout.
        // The same argument covers `connecting`: the only frame we send that
        // the server answers with an error there is our call.accept
        // (not_found after the caller cancelled, not_friends) — ignoring it
        // wedged the callee in "Connecting…", busy-declining fresh invites
        // until the ICE timeout (field 2026-07-18). in_call/reconnecting stay
        // exempt: media-authoritative, and relay frames never draw errors.
        const st = this.machine.state;
        if (
          (st.phase === 'dialing' || st.phase === 'ringing_remote' || st.phase === 'connecting') &&
          st.call
        ) {
          this.drive({ t: 'remoteEnd', callId: st.call.callId, reason: 'error' });
        }
        this.deps.onFrame?.(frame);
        return;
      }
      default:
        this.deps.onFrame?.(frame);
    }
  }

  private enqueue(work: () => Promise<void>): void {
    this.pipeline = this.pipeline.then(work).catch(() => this.abortBrokenCall());
  }

  /**
   * Reconcile an authoritative call.sync snapshot (docs/02 §4; TODO.md #4). The
   * decision is a pure function (reconcile.ts); this is the thin glue that
   * executes it. Idempotent: redundant snapshots on every reconnect are safe.
   */
  private onSync(p: CallSyncPayload): void {
    const m = this.machine.state;
    const action = reconcileSync(
      { phase: m.phase, callId: m.call?.callId ?? null, role: m.call?.role ?? null },
      p,
      this.timers.now(),
    );
    switch (action.t) {
      case 'none':
        return;
      case 'endLocalCall': {
        // Server-authoritative end of a ring whose cancel we lost: drive the
        // machine's normal remote-end path (no frame is sent — the call is
        // already gone server-side). mapRemoteEnd renders the missed/lost cause.
        const call = this.machine.state.call;
        if (call) this.drive({ t: 'remoteEnd', callId: call.callId, reason: action.reason });
        return;
      }
      case 'injectInvite':
        // Through the NORMAL invite path (verify + dedupe + terminal guards),
        // bounded to the remaining ring window.
        this.enqueue(() => this.onInvite(action.invite, action.ringMs));
        return;
      case 'sendEnd':
        // Clear a server-side phantom (we're idle; it tracks us in a call).
        this.client.send({
          type: 'call.end',
          payload: { callId: action.callId, reason: 'hangup' },
        });
        return;
    }
  }

  private async onInvite(p: CallInvitePayload, ringMsOverride?: number): Promise<void> {
    if (p.to !== this.deps.identity.userId) return;
    // Same fallback as dial()/verificationKeyFor: a directory we cannot reach
    // must not silence the ring. Without the pin, a callee on a stalled link
    // drops the invite and the caller rings out for the full 45 s — the worst
    // possible offline-first failure (docs/00; prime directive).
    const key =
      (await this.deps.contacts.getPublicKey(p.from)) ?? (await this.pins.pinnedKey(p.from));
    if (!key) return; // not an accepted friend — server should never relay this
    const pin = await this.pins.checkAndPin(p.from, key);
    if (pin.status === 'changed') {
      // Key-change interlock: block ringing until the user decides (docs/03 §4.2).
      this.store.patch({
        keyChange: { peerUserId: p.from, pinnedKeyB64: pin.pinnedKeyB64, newKeyB64: key },
      });
      this.client.send({
        type: 'call.decline',
        payload: { callId: p.callId, reason: 'declined' },
      });
      return;
    }
    const pinnedKey = (await this.pins.pinnedKey(p.from)) ?? key;
    const ok = await verifySdpFingerprint({
      senderPublicKeyB64: pinnedKey,
      callId: p.callId,
      senderUuid: p.from,
      recipientUuid: this.deps.identity.userId,
      sdp: p.sdp,
      fingerprintSigB64: p.fingerprintSig,
    });
    if (!ok) {
      // Abort with a security error, never silently degrade (docs/03 §4.1).
      this.client.send({ type: 'call.end', payload: { callId: p.callId, reason: 'error' } });
      return;
    }
    if (this.cachedInvites.size >= MAX_CACHED_INVITES) {
      const oldest = this.cachedInvites.keys().next().value;
      if (oldest !== undefined) this.cachedInvites.delete(oldest);
    }
    this.cachedInvites.set(p.callId, p);
    // Bound the ring to the remaining server window when a deadline is known:
    // the override (from a call.sync snapshot) wins, else the invite's own
    // optional expiresAt, else undefined ⇒ full RING_MS (today's behaviour).
    const ringMs = ringMsOverride ?? remainingRingMs(p.expiresAt, this.timers.now());
    this.drive({
      t: 'invite',
      callId: p.callId,
      from: p.from,
      ...(ringMs !== undefined ? { ringMs } : {}),
    });
  }

  private async onAccept(p: CallAcceptPayload): Promise<void> {
    const call = this.machine.state.call;
    if (!call || call.callId !== p.callId) {
      // An accept for a call we are not (or no longer) in: our hangup for it
      // was lost in transit and the callee answered the ghost — the server
      // just pinned it "answered" with nobody on this side. Silence here
      // leaves both users auto-declined "busy" for a call neither is in and
      // the callee hanging in connecting (field 2026-07-18). Reply with an
      // end: the server verifies participation, so this can only clear a
      // call this user owns — and never our live call (callId differs).
      this.client.send({ type: 'call.end', payload: { callId: p.callId, reason: 'hangup' } });
      return;
    }
    if (call.role !== 'caller') return;
    // Idempotent accept: the answer may be applied only while we are still
    // waiting for it (dialing / ringing_remote). A duplicate or replayed
    // call.accept — push + socket, or a resume-replay whose seq numbering
    // restarted — arriving after we already advanced to connecting/in_call is
    // dropped here: a second setRemoteDescription(answer) on a non-offer state
    // throws and would abort a live, connected call (Connection > everything).
    if (this.machine.state.phase !== 'dialing' && this.machine.state.phase !== 'ringing_remote') {
      return;
    }
    const key = await this.verificationKeyFor(call.peer);
    const ok =
      key !== null &&
      (await verifySdpFingerprint({
        senderPublicKeyB64: key,
        callId: p.callId,
        senderUuid: call.peer,
        recipientUuid: this.deps.identity.userId,
        sdp: p.sdp,
        fingerprintSigB64: p.fingerprintSig,
      }));
    if (!ok) {
      this.drive({ t: 'securityAbort' });
      return;
    }
    this.drive({ t: 'remoteAccept', callId: p.callId });
    const pc = this.peer?.pc;
    if (pc) {
      await pc.setRemoteDescription({ type: 'answer', sdp: p.sdp });
      this.remoteApplied = true;
      await this.applyHeldCandidates(p.callId);
    }
  }

  private async onIce(p: CallIcePayload): Promise<void> {
    const call = this.machine.state.call;
    const applicable =
      call?.callId === p.callId && this.peerCallId === p.callId && this.remoteApplied && this.peer;
    if (!applicable) {
      // Hold (bounded): remote description not applied yet, or not the
      // current call — e.g. the incoming half of a crossed call whose peer
      // object only materializes when the deferred openPeer effect runs.
      this.holdRemoteIce(p.callId, p.candidates);
      return;
    }
    for (const candidate of p.candidates) {
      await this.peer!.pc.addIceCandidate(candidate).catch(() => {
        // Stale candidates after an ignored glare offer are expected noise.
      });
    }
  }

  private async onRenegotiate(p: CallRenegotiatePayload): Promise<void> {
    const call = this.machine.state.call;
    const pc = this.peer?.pc;
    if (!call || call.callId !== p.callId || !pc) return;
    const key = await this.verificationKeyFor(call.peer);
    const ok =
      key !== null &&
      (await verifySdpFingerprint({
        senderPublicKeyB64: key,
        callId: p.callId,
        senderUuid: call.peer,
        recipientUuid: this.deps.identity.userId,
        sdp: p.sdp,
        fingerprintSigB64: p.fingerprintSig,
      }));
    if (!ok) {
      this.drive({ t: 'securityAbort' });
      return;
    }

    const type =
      sdpTypeFromMarker(p.sdp) ?? (pc.signalingState === 'stable' ? 'offer' : 'answer');
    if (type === 'offer') {
      // Perfect negotiation (docs/01 §5.2): the impolite (lower-UUID) peer
      // ignores glare offers; the polite peer rolls back and answers.
      const collision = this.makingOffer || pc.signalingState !== 'stable';
      if (!this.polite && collision) return;
      await pc.setRemoteDescription({ type: 'offer', sdp: p.sdp });
      this.remoteApplied = true;
      await this.applyHeldCandidates(call.callId);
      const answer = await pc.createAnswer();
      const sdp = withSdpTypeMarker(await this.setShapedLocalDescription(pc, answer), 'answer');
      const fingerprintSig = await signSdpFingerprint(this.deps.identity, {
        callId: call.callId,
        toUuid: call.peer,
        sdp,
      });
      this.client.send({
        type: 'call.renegotiate',
        payload: { callId: call.callId, sdp, fingerprintSig },
      });
    } else if (pc.signalingState === 'have-local-offer') {
      await pc.setRemoteDescription({ type: 'answer', sdp: p.sdp });
    }
  }

  private async verificationKeyFor(peer: string): Promise<string | null> {
    return (await this.pins.pinnedKey(peer)) ?? this.deps.contacts.getPublicKey(peer);
  }

  private holdRemoteIce(callId: string, candidates: IceCandidate[]): void {
    if (!this.pendingRemoteIce.has(callId) && this.pendingRemoteIce.size >= 4) {
      const oldest = this.pendingRemoteIce.keys().next().value;
      if (oldest !== undefined) this.pendingRemoteIce.delete(oldest);
    }
    const held = this.pendingRemoteIce.get(callId) ?? [];
    this.pendingRemoteIce.set(callId, [...held, ...candidates]);
  }

  private async applyHeldCandidates(callId: string): Promise<void> {
    const held = this.pendingRemoteIce.get(callId);
    if (!held) return;
    this.pendingRemoteIce.delete(callId);
    for (const candidate of held) {
      await this.peer?.pc.addIceCandidate(candidate).catch(() => {
        // ignore unusable held candidates
      });
    }
  }

  // --- ICE / quality ----------------------------------------------------------------

  private onIceState(state: string): void {
    switch (state) {
      case 'connected':
      case 'completed': {
        const phase = this.machine.state.phase;
        if (this.aux.connectedAt === null) this.aux.connectedAt = this.timers.now();
        this.aux.reconnectingSince = null;
        this.monitor.start();
        if (phase === 'connecting' || phase === 'reconnecting') this.drive({ t: 'iceConnected' });
        break;
      }
      case 'disconnected':
        this.drive({ t: 'iceDisconnected' });
        break;
      case 'failed': {
        // connecting: iceFailed → immediate restart (dead air otherwise);
        // reconnecting: iceFailed → next restart within the 60 s budget;
        // in_call: degrade to reconnecting via iceDisconnected.
        const phase = this.machine.state.phase;
        if (phase === 'reconnecting' || phase === 'connecting') this.drive({ t: 'iceFailed' });
        else this.drive({ t: 'iceDisconnected' });
        break;
      }
      default:
        break;
    }
    this.publish();
  }

  private onQualityDecision(d: LadderDecision, s: StatsSample): void {
    this.aux.rung = d.rung;
    this.aux.bar = d.bar;
    this.aux.viaRelay = s.usingRelay;
    if (d.rung !== this.appliedRung) {
      this.appliedRung = d.rung;
      this.applyRungConfig(d.config);
    }
    this.publish();
  }

  private applyRungConfig(config: RungConfig): void {
    const pc = this.peer?.pc;
    if (!pc) return;
    for (const sender of pc.getSenders?.() ?? []) {
      try {
        const params = sender.getParameters?.() ?? {};
        const encodings = params.encodings?.length ? params.encodings : [{}];
        for (const enc of encodings) enc['maxBitrate'] = config.maxBitrateBps;
        void sender.setParameters?.({ ...params, encodings });
      } catch {
        // parameter application is best-effort; the ladder retries every rung change
      }
    }
    for (const receiver of pc.getReceivers?.() ?? []) {
      if ('jitterBufferTarget' in receiver) receiver.jitterBufferTarget = config.jitterBufferTargetMs;
    }
    // Frame size, playback bandwidth and RED live only in SDP, so a rung that
    // changes them has no effect until a new local description reaches the peer.
    // Without this, a call that starts healthy and degrades onto EDGE keeps the
    // 20 ms framing it was set up with — and header overhead, not bitrate, is
    // what makes 20 ms unaffordable there (rtc/sdp.ts). Deliberately NOT an ICE
    // restart: throwing away a working candidate pair to change a codec
    // parameter inverts the prime directive.
    //
    // Skipped when this same transition flips the relay policy. The ladder
    // climbs straight to its target, so a jump to rung 4 changes the Opus shape
    // AND forces relay; two offers would then go out back to back, the peer
    // would answer the codec one first, and the answer belonging to the ICE
    // restart would arrive in `stable` and be dropped — leaving us restarted
    // onto relay with the peer's pre-restart ICE credentials, i.e. the ladder's
    // last resort before disconnection broken by a codec optimisation. The
    // restart's own offer already carries the new shape (setShapedLocalDescription
    // reads the current rung), so nothing is lost by skipping it here.
    const relayChanging = config.forceRelay !== this.relayOnly;
    if (this.sdpShapeChanged(config) && !relayChanging) this.renegotiateForRung();

    if (config.forceRelay && !this.relayOnly) {
      // Rung 4: restart ICE onto TURN/TCP/TLS (docs/01 §5). Keep the TURN
      // servers — a relay-only policy with no iceServers can gather nothing.
      this.relayOnly = true;
      pc.setConfiguration?.({ iceServers: this.currentIceServers, iceTransportPolicy: 'relay' });
      this.drive({ t: 'networkChange' });
    } else if (!config.forceRelay && this.relayOnly) {
      // Recovered: restart back toward faster paths (docs/04 §3), still
      // carrying the TURN servers so relay stays available as a fallback.
      this.relayOnly = false;
      pc.setConfiguration?.({ iceServers: this.currentIceServers, iceTransportPolicy: 'all' });
      this.drive({ t: 'networkChange' });
    }
  }

  // --- state publication ----------------------------------------------------------

  /**
   * Start/stop the owned tones for the machine's current phase. Called from
   * publish(), which runs after every event and effect batch — including every
   * terminal one (hangup, decline, remote end, security/media abort, the
   * reconnect-budget concession), all of which leave the machine with no call
   * and therefore stop both tones.
   */
  private syncTones(): void {
    const tones = this.tones;
    if (!tones) return;
    const m = this.machine.state;
    const reconnect = m.call !== null && m.phase === 'reconnecting';
    const ringback =
      m.call?.role === 'caller' && (m.phase === 'dialing' || m.phase === 'ringing_remote');
    if (reconnect !== this.reconnectToneOn) {
      this.reconnectToneOn = reconnect;
      if (reconnect) tones.reconnect.start();
      else tones.reconnect.stop();
    }
    if (ringback !== this.ringbackOn) {
      this.ringbackOn = ringback;
      if (ringback) tones.ringback.start();
      else tones.ringback.stop();
    }
  }

  private stopTones(): void {
    if (this.reconnectToneOn) {
      this.reconnectToneOn = false;
      this.tones?.reconnect.stop();
    }
    if (this.ringbackOn) {
      this.ringbackOn = false;
      this.tones?.ringback.stop();
    }
  }

  private publish(): void {
    this.syncTones();
    const m = this.machine.state;
    const prev = this.store.get();
    if (!m.call) {
      if (prev.call !== null) this.store.patch({ call: null });
      return;
    }
    if (m.phase === 'reconnecting' && this.aux.reconnectingSince === null) {
      this.aux.reconnectingSince = this.timers.now();
    }
    if (m.phase !== 'reconnecting') this.aux.reconnectingSince = null;
    const call: ActiveCallState = {
      callId: m.call.callId,
      peerUserId: m.call.peer,
      direction: m.call.role === 'caller' ? 'outgoing' : 'incoming',
      phase: m.phase,
      connectedAt: this.aux.connectedAt,
      reconnectingSince: this.aux.reconnectingSince,
      muted: this.muted,
      speaker: this.speaker,
      audioRequest: this.audioRequest,
      rung: this.aux.rung,
      bar: this.aux.bar,
      viaRelay: this.aux.viaRelay,
    };
    this.store.patch({ call });
  }
}
